import React, { useState } from "react";
import payrollService from "../../services/payrollService";
import SalaryBreakdown from "../../components/Payroll/SalaryBreakdown";
import Notification from "../../components/UI/Notification";
import { Search, User, Calendar, DollarSign, TrendingUp, CheckCircle, XCircle } from "lucide-react";

const EmployeeSalaryStructure = () => {
  const [employeeId, setEmployeeId] = useState("");
  const [structure, setStructure] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const fetchStructure = async () => {
    if (!employeeId.trim()) {
      setNotification({ type: "warning", message: "Please enter employee ID" });
      return;
    }
    setLoading(true);
    try {
      const response = await payrollService.getEmployeeSalaryStructure(employeeId);
      // Check for successful response based on message or data presence
      if (response.message && response.message.includes("successfully") && response.data) {
        // Handle both direct data and nested data structures
        const structureData = response.data?.data || response.data;
        setStructure(structureData);
      } else {
        setNotification({ type: "error", message: response.message || "Failed to fetch salary structure" });
        setStructure(null);
      }
    } catch (error) {
      console.error("Error fetching salary structure:", error);
      setNotification({ type: "error", message: "Failed to fetch salary structure" });
      setStructure(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Present";
    return new Date(dateString).toLocaleDateString("en-IN");
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString("en-IN")}`;
  };

  const getStatusBadge = (isActive) => {
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
        isActive
          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
          : "bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300"
      }`}>
        {isActive ? (
          <>
            <CheckCircle size={12} className="mr-1" />
            Active
          </>
        ) : (
          <>
            <XCircle size={12} className="mr-1" />
            Inactive
          </>
        )}
      </span>
    );
  };

  return (
    <div className="p-6 font-poppins">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Employee Salary Structure
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          View and manage employee salary structure
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && fetchStructure()}
              placeholder="Enter Employee ID..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
          <button
            onClick={fetchStructure}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50"
          >
            {loading ? "Searching..." : "Search"}
          </button>
        </div>
      </div>

      {loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading salary structure...</p>
        </div>
      )}

      {structure && (
        <div className="space-y-6">
          {/* Employee Information Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Employee Information
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  Complete salary structure details for {structure.employee?.full_name || structure.user?.name}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <User size={20} className="text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Employee ID</span>
                </div>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {structure.employee_id}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <DollarSign size={20} className="text-green-600 mr-2" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Basic Salary</span>
                </div>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(structure.basic_salary)}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <TrendingUp size={20} className="text-purple-600 mr-2" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Gross Salary</span>
                </div>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(structure.gross_salary)}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Calendar size={20} className="text-orange-600 mr-2" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">CTC</span>
                </div>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(structure.ctc)}
                </p>
              </div>
            </div>

            {/* Employee Details */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Personal Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Full Name:</span>
                    <span className="font-medium">{structure.employee?.full_name || structure.user?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Email:</span>
                    <span className="font-medium">{structure.user?.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Gender:</span>
                    <span className="font-medium capitalize">{structure.employee?.gender}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">PAN No:</span>
                    <span className="font-medium">{structure.employee?.pan_no}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Aadhar No:</span>
                    <span className="font-medium">{structure.employee?.aadhar_no}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Blood Group:</span>
                    <span className="font-medium">{structure.employee?.blood_group}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Marital Status:</span>
                    <span className="font-medium capitalize">{structure.employee?.marital_status}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Salary Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Effective From:</span>
                    <span className="font-medium">{formatDate(structure.effective_from)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Effective To:</span>
                    <span className="font-medium">{formatDate(structure.effective_to)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Status:</span>
                    {getStatusBadge(structure.is_active)}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Created By:</span>
                    <span className="font-medium">{structure.creator?.name}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Address and Family Information */}
            <div className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Address Information - Left Column */}
                {structure.employee?.address && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Address</h4>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <p className="text-sm text-gray-700 dark:text-gray-300">{structure.employee.address}</p>
                    </div>
                  </div>
                )}

                {/* Family Information - Right Column */}
                {(structure.employee?.father_name || structure.employee?.mother_name || structure.employee?.spouse_name) && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Family Information</h4>
                    <div className="space-y-3">
                      {structure.employee?.father_name && (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                          <p className="text-xs text-gray-600 dark:text-gray-400">Father's Name</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{structure.employee.father_name}</p>
                        </div>
                      )}
                      {structure.employee?.mother_name && (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                          <p className="text-xs text-gray-600 dark:text-gray-400">Mother's Name</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{structure.employee.mother_name}</p>
                        </div>
                      )}
                      {structure.employee?.spouse_name && (
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                          <p className="text-xs text-gray-600 dark:text-gray-400">Spouse's Name</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{structure.employee.spouse_name}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Statutory Components Toggle */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Statutory Components
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className={`p-4 rounded-lg border-2 ${structure.pf_enabled ? 'border-green-200 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 bg-gray-50 dark:bg-gray-700'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Provident Fund</span>
                  {structure.pf_enabled ? <CheckCircle size={20} className="text-green-600" /> : <XCircle size={20} className="text-gray-400" />}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">12% of Basic</p>
              </div>

              <div className={`p-4 rounded-lg border-2 ${structure.esi_enabled ? 'border-green-200 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 bg-gray-50 dark:bg-gray-700'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">ESI</span>
                  {structure.esi_enabled ? <CheckCircle size={20} className="text-green-600" /> : <XCircle size={20} className="text-gray-400" />}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">0.75% of Gross</p>
              </div>

              <div className={`p-4 rounded-lg border-2 ${structure.pt_enabled ? 'border-green-200 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 bg-gray-50 dark:bg-gray-700'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Professional Tax</span>
                  {structure.pt_enabled ? <CheckCircle size={20} className="text-green-600" /> : <XCircle size={20} className="text-gray-400" />}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">₹200 per month</p>
              </div>

              <div className={`p-4 rounded-lg border-2 ${structure.nps_enabled ? 'border-green-200 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 bg-gray-50 dark:bg-gray-700'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">NPS</span>
                  {structure.nps_enabled ? <CheckCircle size={20} className="text-green-600" /> : <XCircle size={20} className="text-gray-400" />}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">10% of Basic</p>
              </div>

              <div className={`p-4 rounded-lg border-2 ${structure.gratuity_enabled ? 'border-green-200 bg-green-50 dark:bg-green-900/20' : 'border-gray-200 bg-gray-50 dark:bg-gray-700'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Gratuity</span>
                  {structure.gratuity_enabled ? <CheckCircle size={20} className="text-green-600" /> : <XCircle size={20} className="text-gray-400" />}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">15 days per year</p>
              </div>
            </div>
          </div>

          {/* Salary Breakdown */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Salary Breakdown
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Earnings Column */}
              <div>
                <h4 className="text-md font-medium text-green-600 dark:text-green-400 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                  Earnings
                </h4>
                <div className="space-y-2">
                  {structure.earnings_components && structure.earnings_components.length > 0 ? (
                    structure.earnings_components.map((component, index) => (
                      <div key={index} className="flex justify-between items-center py-2 px-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {component.component_name}
                        </span>
                        <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                          {formatCurrency(component.amount)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                      No earnings components
                    </div>
                  )}
                  <div className="border-t border-green-200 dark:border-green-700 pt-2 mt-3">
                    <div className="flex justify-between items-center py-2 px-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        Total Earnings
                      </span>
                      <span className="text-sm font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(structure.earnings_components?.reduce((sum, comp) => sum + (comp.amount || 0), 0) || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Deductions Column */}
              <div>
                <h4 className="text-md font-medium text-red-600 dark:text-red-400 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4m16 0l-4-4m4 4l-4 4M4 12l4-4m-4 4l4 4" />
                  </svg>
                  Deductions
                </h4>
                <div className="space-y-2">
                  {structure.deduction_components && structure.deduction_components.length > 0 ? (
                    structure.deduction_components.map((component, index) => (
                      <div key={index} className="flex justify-between items-center py-2 px-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {component.component_name}
                        </span>
                        <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                          {formatCurrency(component.amount)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                      No deduction components
                    </div>
                  )}
                  <div className="border-t border-red-200 dark:border-red-700 pt-2 mt-3">
                    <div className="flex justify-between items-center py-2 px-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        Total Deductions
                      </span>
                      <span className="text-sm font-bold text-red-600 dark:text-red-400">
                        {formatCurrency(structure.deduction_components?.reduce((sum, comp) => sum + (comp.amount || 0), 0) || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Net Salary Summary */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center py-3 px-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  Net Salary
                </span>
                <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                  {formatCurrency(structure.gross_salary || 0)}
                </span>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Additional Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Salary Adjustments</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Original Basic Salary:</span>
                    <span className="font-medium">{structure.original_basic_salary ? formatCurrency(structure.original_basic_salary) : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Applied Percentage:</span>
                    <span className="font-medium">{structure.applied_percentage ? `${structure.applied_percentage}%` : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Applied Fixed Amount:</span>
                    <span className="font-medium">{structure.applied_fixed ? formatCurrency(structure.applied_fixed) : 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Audit Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Created At:</span>
                    <span className="font-medium">{formatDate(structure.created_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Updated At:</span>
                    <span className="font-medium">{formatDate(structure.updated_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Updated By:</span>
                    <span className="font-medium">{structure.updater?.name || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!structure && !loading && employeeId && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <User size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Salary Structure Found
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No salary structure found for employee {employeeId}.
          </p>
        </div>
      )}

    </div>
  );
};

export default EmployeeSalaryStructure;

